import React, { useContext, useState, ChangeEvent, FocusEvent } from 'react';

import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

import TextInput from 'components/common/TextInput/TextInput';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import Button from 'components/common/Button/Button';
import { RootStoreContext } from 'index';
import { EditIcon } from 'components/common/Icons/Icons';
import useForm from 'hooks/useForm';
import { isValidPhone, validateRequiredField } from 'common/utils/validation.utils';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { PersonalInformationFormValues } from 'components/myProfile/PersonalInformation/personalInformation.types';

import { PersonalInformationFormInput } from './personalInformation.enums';
import mainStyles from '../MyProfile.module.scss';
import styles from './PersonalInformation.module.scss';

const PersonalInformation = () => {
  const { authStore } = useContext(RootStoreContext);
  const { account, isLoading } = authStore;
  const [isEditDataActive, setIsEditDataActive] = useState(false);

  const roles = account!.roles.join(', ');
  const isMainContact = account?.isMainContact ? 'Yes' : 'No';

  const { errors, values, setValue, validateField, handleSubmit, resetForm } = useForm<PersonalInformationFormValues>({
    defaultValues: {
      [PersonalInformationFormInput.FirstName]: account?.firstName || '',
      [PersonalInformationFormInput.LastName]: account?.lastName || '',
      [PersonalInformationFormInput.Phone]: account?.phone || '',
      [PersonalInformationFormInput.Position]: account?.position || '',
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case PersonalInformationFormInput.FirstName:
          return validateRequiredField(value, fieldName);
        case PersonalInformationFormInput.LastName:
          return validateRequiredField(value, fieldName);
        case PersonalInformationFormInput.Phone:
          return !!value ? isValidPhone(value) : '';
      }

      return '';
    },
  });

  const handleEditData = () => setIsEditDataActive(true);

  const handleCancelEditData = () => {
    resetForm();
    setIsEditDataActive(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const name = event.target.name as keyof typeof values;

    validateField(name, values[name]);
  };

  const handleSaveInformation = handleSubmit(async () => {
    try {
      await authStore.updatePersonalInformation({
        firstName: values[PersonalInformationFormInput.FirstName],
        lastName: values[PersonalInformationFormInput.LastName],
        phone: values[PersonalInformationFormInput.Phone] || null,
        position: values[PersonalInformationFormInput.Position] || null,
      });

      notify(ToastMessage.InformationSaved, 'success');
      setIsEditDataActive(false);
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    }
  });

  return (
    <div className={mainStyles.mainContainer}>
      <div className={mainStyles.inputsContainer}>
        <div className={mainStyles.header}>
          <div className={mainStyles.headerLabel}>Personal Information</div>
          {!isEditDataActive && (
            <Button onClick={handleEditData} className={mainStyles.editButton} variant="link">
              <EditIcon /> Edit Data
            </Button>
          )}
        </div>
        <div className={styles.formInput}>
          <TextInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={PersonalInformationFormInput.FirstName}
            name={PersonalInformationFormInput.FirstName}
            value={values[PersonalInformationFormInput.FirstName]}
            errorMessage={errors[PersonalInformationFormInput.FirstName]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={!isEditDataActive}
          />
          <TextInput
            className={styles.input}
            useRequiredSymbol={isEditDataActive}
            containerClassName={styles.inputContainer}
            labelTitle={PersonalInformationFormInput.LastName}
            name={PersonalInformationFormInput.LastName}
            value={values[PersonalInformationFormInput.LastName]}
            errorMessage={errors[PersonalInformationFormInput.LastName]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={!isEditDataActive}
          />
        </div>
        <div className={styles.formInput}>
          <PhoneNumberInput
            className={styles.input}
            containerClassName={styles.inputContainer}
            labelTitle={PersonalInformationFormInput.Phone}
            name={PersonalInformationFormInput.Phone}
            value={values[PersonalInformationFormInput.Phone]}
            errorMessage={errors[PersonalInformationFormInput.Phone]}
            onChange={(value: any) => setValue(PersonalInformationFormInput.Phone, value)}
            onBlur={handleBlur}
            disabled={!isEditDataActive}
          />
          <TextInput
            className={styles.input}
            containerClassName={styles.inputContainer}
            labelTitle={PersonalInformationFormInput.Position}
            name={PersonalInformationFormInput.Position}
            value={values[PersonalInformationFormInput.Position]}
            onChange={handleChange}
            disabled={!isEditDataActive}
          />
        </div>
        <div className={styles.formInput}>
          <TextInput
            className={clsx(styles.input, styles.disabledInput)}
            containerClassName={styles.inputContainer}
            labelTitle="Role(s)"
            name="roles"
            value={roles}
            disabled
          />
          <TextInput
            className={clsx(styles.input, styles.disabledInput)}
            containerClassName={styles.inputContainer}
            labelTitle="Main Contact"
            name="mainContact"
            value={isMainContact}
            disabled
            tooltipTitle="This field is set by your organization's user manager and they should be approached to address changes."
          />
        </div>
        <div className={styles.formInput}>
          {isEditDataActive && (
            <div className={mainStyles.editButtons}>
              <Button onClick={handleSaveInformation} isLoading={isLoading}>
                Save Changes
              </Button>
              <Button variant="white" onClick={handleCancelEditData}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(PersonalInformation);
